// CRITICAL: Check for password recovery BEFORE any imports that might process auth tokens
// This must run synchronously before Supabase client is created
if (typeof window !== 'undefined') {
  const hash = window.location.hash;
  if (hash && hash.includes('type=recovery') && !window.location.pathname.includes('/reset-password')) {
    // Redirect to reset-password with the hash tokens before Supabase processes them
    window.location.replace('/reset-password' + hash);
  }
  // Capture referral code from ?ref= on ANY page (not just Auth)
  const params = new URLSearchParams(window.location.search);
  // This ensures the code is stored even if the user lands on homepage, jobs, etc.
  const refParam = params.get('ref');
  if (refParam) {
    try { localStorage.setItem('referral_code', refParam); } catch {}
  }
  // Where this visitor came from (ad / share link / PESO / Google / direct) + Facebook click id
  captureAttribution(window.location, document.referrer);
}

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { supabase, getSessionWithTimeout, isPWAMode, withTimeout } from './lib/supabase';
import api from './lib/api';
import { captureAttribution } from './lib/attribution';
import { Toaster, toast } from './components/ui/sonner';
// Offline functionality removed - was causing iOS issues
// import { setupOnlineOfflineListeners } from './lib/registerSW';
import { setupNotificationListeners, syncExistingSubscription } from './lib/oneSignalService';
// PWAUpdatePrompt removed - no longer caching/updating
// import PWAUpdatePrompt from './components/PWAUpdatePrompt';
import PWAInstallBanner from './components/PWAInstallBanner';
import NotificationPromptModal from './components/NotificationPromptModal';
// NetworkStatusBanner removed - no offline mode
// import NetworkStatusBanner from './components/NetworkStatusBanner';
import WwwRedirect from './components/WwwRedirect';
import DebugPanel from './components/DebugPanel';
import CookieConsent from './components/CookieConsent';
// LanguageSelectionModal moved to Jobs.js (only shows on landing/browse page)
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import MaintenancePage from './components/MaintenancePage';
import { useNotificationPrompt } from './hooks/useNotificationPrompt';
import './i18n'; // Initialize i18n

// Component to redirect /auth to /sign-up while preserving query params
const AuthRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/sign-up${location.search}`} replace />;
};

// All pages loaded as regular imports (single bundle — no chunk errors after deploys)
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import RoleSelection from './pages/RoleSelection';
import ProfileCompletion from './pages/ProfileCompletion';
import DiagnosticAuth from './pages/DiagnosticAuth';
import CheckEmail from './pages/CheckEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Services from './pages/Services';
import CreateServiceListing from './pages/CreateServiceListing';
import EditServiceListing from './pages/EditServiceListing';
import ServiceListingDetail from './pages/ServiceListingDetail';
import PostServiceRequest from './pages/PostServiceRequest';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import JobDetails from './pages/JobDetails';
import EmployerProfile from './pages/EmployerProfile';
import PostJob from './pages/PostJob';
import GigDetails from './pages/GigDetails';
import ProfileNew from './pages/ProfileNew';
import Credits from './pages/Credits';
import Messages from './pages/Messages';
import Admin from './pages/Admin';
import AdminDashboard from './pages/AdminDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import AppliedJobs from './pages/AppliedJobs';
import VerifyID from './pages/VerifyID';
import CEODashboard from './pages/CEODashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PanglaoPesoLanding from './pages/peso/PanglaoPesoLanding';
import PesoSeekerRegister from './pages/peso/PesoSeekerRegister';
import PesoEmployerRegister from './pages/peso/PesoEmployerRegister';
import { PesoOfficeProvider } from './pages/peso/PesoOfficeContext';
import TermsAgreement from './pages/TermsAgreement';
import DataDeletion from './pages/DataDeletion';
import NotificationDebug from './pages/NotificationDebug';

// Role-specific dashboards
import DashboardSeeker from './pages/DashboardSeeker';
import DashboardEmployer from './pages/DashboardEmployer';
import PesoDashboard from './pages/peso/PesoDashboard';
import PesoNsrpPrint from './pages/peso/PesoNsrpPrint';
import PesoNsrp2Print from './pages/peso/PesoNsrp2Print';
import DashboardContractor from './pages/DashboardContractor';
import DashboardOwner from './pages/DashboardOwner';

function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileFetchFailed, setProfileFetchFailed] = useState(false); // Track if profile fetch failed
  const [serviceDown, setServiceDown] = useState(false); // Supabase/service unavailable
  const loadingRef = React.useRef(true); // Use ref to track loading state for safety timeout
  
  // Notification prompt hook
  const { showPrompt, handleClose } = useNotificationPrompt(profile);
  
  // Keep ref in sync with state
  React.useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Force light theme on all browsers (including Samsung Browser)
  useEffect(() => {
    const forceTheme = () => {
      document.documentElement.classList.remove('dark');
      document.documentElement.style.colorScheme = 'light';
      try { localStorage.removeItem('theme'); } catch {}
    };

    // Apply immediately
    forceTheme();
    
    // Watch for changes (in case browser tries to apply dark mode)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (document.documentElement.classList.contains('dark')) {
            forceTheme();
          }
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  // Handle OAuth/auth errors that arrive as URL query params
  // (e.g., ?error=invalid_request&error_code=bad_oauth_state)
  // Without this, users see a normal landing page with zero feedback.
  useEffect(() => {
    try {
      // Skip on /auth/callback — AuthCallback.js handles its own errors
      // (otherwise both show error messages for the same failure)
      if (window.location.pathname === '/auth/callback') return;

      const params = new URLSearchParams(window.location.search);
      const errorCode = params.get('error_code');
      const error = params.get('error');

      if (errorCode || error) {
        const errorDesc = params.get('error_description');
        // Clean URL immediately
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        url.searchParams.delete('error_code');
        url.searchParams.delete('error_description');
        window.history.replaceState(null, '',
          url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash
        );
        // Show user-friendly message after toast system initializes
        setTimeout(() => {
          if (errorCode === 'bad_oauth_state') {
            toast.error('Your login session expired. Please try signing in again.');
          } else if (errorCode === 'otp_expired') {
            toast.error('This link has expired. Please request a new one.');
          } else if (errorDesc) {
            toast.error(decodeURIComponent(errorDesc.replace(/\+/g, ' ')));
          } else {
            toast.error('Sign-in failed. Please try again.');
          }
        }, 500);
      }
    } catch (e) { /* URL parsing error — ignore */ }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Note: Password recovery detection is now handled at module level (top of file)
    // to run before Supabase client auto-processes tokens

    // Check active session with timeout (prevents PWA infinite loading)
    const initializeAuth = async () => {
      try {
        console.log('[App] Initializing auth...', isPWAMode() ? '(PWA mode)' : '(browser mode)');
        
        // Use timeout-wrapped session getter to prevent PWA hanging
        const { session, error } = await getSessionWithTimeout(
          isPWAMode() ? 3000 : 5000  // Shorter timeout for PWA
        );
        
        if (!mounted) return;
        
        if (error === 'timeout') {
          console.warn('[App] Session initialization timed out, proceeding as logged out');
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        
        if (error) {
          console.error('[App] Session error:', error);
        }
        
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('[App] Auth initialization failed:', err);
        if (mounted) {
          // Detect network/service outage errors
          const msg = (err?.message || '').toLowerCase();
          const isServiceDown = msg.includes('fetch') || msg.includes('network') ||
            msg.includes('502') || msg.includes('503') || msg.includes('522') ||
            msg.includes('connection') || msg.includes('failed to fetch') ||
            err?.name === 'TypeError';
          if (isServiceDown) {
            setServiceDown(true);
          }
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      console.log('[App] Auth state changed:', _event);

      // Handle password recovery - redirect to reset password page
      if (_event === 'PASSWORD_RECOVERY') {
        console.log('[App] Password recovery event detected, redirecting to reset password');
        window.location.href = '/reset-password';
        return;
      }

      setUser(session?.user ?? null);
      if (session?.user) {
        setProfileFetchFailed(false); // Reset on new login
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setProfileFetchFailed(false); // Reset on logout
        setLoading(false);
      }
    });

    // Safety timeout: If loading takes more than 8 seconds, force complete
    // This prevents users from being stuck on loading screen forever
    const safetyTimeout = setTimeout(() => {
      if (mounted && loadingRef.current) {
        console.warn('[App] Safety timeout triggered - forcing loading complete');
        setLoading(false);
      }
    }, 8000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      clearTimeout(safetyTimeout);
    };
  }, []);

  // Initialize OneSignal push notifications
  useEffect(() => {
    // Note: We DON'T show toast notifications when app is open
    // The app already has a notification system (bell icon)
    setupNotificationListeners((payload) => {
      console.log('[App] Foreground notification received:', payload);
      // Do nothing - app has its own notification indicator
    });
    console.log('✅ OneSignal push notifications initialized');
  }, []);

  const fetchProfile = async (userId) => {
    // Wrap profile fetch with 10 second timeout to prevent mobile hangs
    const fetchWithTimeout = async () => {
      try {
        // Get the user's email for fallback matching
        const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      // Step 1: Try to find profile by auth_user_id (new method)
      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', userId)
        .limit(1);

      if (error) {
        console.error('Profile fetch error:', error);
      }

      // Step 2: If no profile found by auth_user_id, try email fallback (migration helper)
      if (!data || data.length === 0) {
        console.log('No profile with auth_user_id, trying email fallback:', user.email);
        
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', user.email)
          .limit(1);

        if (!fallbackError && fallbackData && fallbackData.length > 0) {
          const orphanedProfile = fallbackData[0];
          console.log('Found orphaned profile, linking to auth_user_id:', orphanedProfile.id);
          
          // Step 3: Link the orphaned profile to this auth user
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ auth_user_id: userId })
            .eq('id', orphanedProfile.id);

          if (updateError) {
            console.error('Failed to link profile:', updateError);
          } else {
            console.log('✓ Successfully linked orphaned profile to auth user');
            // Use the newly linked profile
            setProfile({ ...orphanedProfile, auth_user_id: userId });
            setLoading(false);
            return;
          }
        }
      }

      // Step 4: Profile found by auth_user_id (normal case)
      if (data && data.length > 0) {
        setProfile(data[0]);
        
        // Update last_login timestamp
        try {
          const { data: session } = await supabase.auth.getSession();
          if (session?.session?.access_token) {
            await api.post('/api/auth/update-login', {}, {
              headers: { Authorization: `Bearer ${session.session.access_token}` }
            });
          }
        } catch (error) {
          console.log('Failed to update last login (non-critical):', error);
        }

        // Sync existing OneSignal subscription to backend if user is already subscribed
        syncExistingSubscription().catch(err => {
          console.error('[App] Error syncing OneSignal subscription:', err);
        });
      } else {
        // Step 5: No profile row. The signup DB trigger normally creates it, but if that
        // trigger fails/does-not-fire the user is left authenticated with NO profile and
        // would be stranded on /role-selection. Self-heal: ask the backend to create the
        // profile from our own auth metadata (idempotent, role-whitelisted), then re-fetch
        // once. This lands PESO/email registrants on their dashboard as intended.
        console.log('No profile found for user, attempting ensure-profile self-heal');
        let healed = null;
        try {
          const { data: sess } = await supabase.auth.getSession();
          const token = sess?.session?.access_token;
          if (token) {
            await api.post('/api/auth/ensure-profile', {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const { data: retry } = await supabase
              .from('profiles')
              .select('*')
              .eq('auth_user_id', userId)
              .limit(1);
            if (retry && retry.length > 0) healed = retry[0];
          }
        } catch (healErr) {
          console.warn('[App] ensure-profile self-heal failed (non-fatal):', healErr);
        }

        if (healed) {
          console.log('✓ Profile self-healed via ensure-profile');
          setProfile(healed);
        } else {
          console.log('No profile found for user (self-heal did not produce one)');
          setProfile(null);
          setProfileFetchFailed(true); // Mark as failed so we don't show infinite loading
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
      setProfileFetchFailed(true); // Mark as failed so we don't show infinite loading
    } finally {
      setLoading(false);
    }
    };

    // Execute with 10 second timeout to prevent mobile hangs
    try {
      await withTimeout(fetchWithTimeout(), 10000, 'Profile fetch timed out');
    } catch (timeoutError) {
      console.error('[App] Profile fetch timed out:', timeoutError);
      setProfile(null);
      setProfileFetchFailed(true); // Mark that fetch failed so we don't show infinite loading
      setLoading(false);
    }
  };

  // Show maintenance page if service is down
  if (serviceDown) {
    return (
      <MaintenancePage onRetry={() => {
        setServiceDown(false);
        setLoading(true);
        window.location.reload();
      }} />
    );
  }

  // Show loading only on initial load
  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  // Stable auth check using user ID
  const isAuthenticated = Boolean(user?.id);
  const hasProfile = Boolean(profile?.id);

  // CRITICAL FIX: If authenticated but profile not loaded yet, show loading
  // But NOT if profile fetch already failed (prevents infinite loading)
  const profileLoading = isAuthenticated && !hasProfile && !profileFetchFailed;

  // Check if profile needs role selection (no role or pending role)
  const needsRoleSelection = hasProfile && (!profile.role || profile.role === 'pending');

  // PESO/NSRP registrants complete the full NSRP Form 1 (far more than onboarding asks
  // for), so they must never be routed to /complete-profile. registration_source is
  // write-once and server-validated (^peso_[a-z0-9_-]{2,40}$), so it can't be spoofed.
  const isPesoRegistrant = Boolean(
    profile && typeof profile.registration_source === 'string' &&
    profile.registration_source.startsWith('peso_')
  );

  // Check if profile needs completion (missing mandatory fields)
  // Only applies if user has a valid role (not pending); never for PESO registrants.
  const needsProfileCompletion = hasProfile && !needsRoleSelection && profile.role !== 'peso_staff' && !isPesoRegistrant && (
    !profile.phone || profile.phone.trim() === '' ||
    !profile.town || profile.town.trim() === '' ||
    ((profile.role === 'job_seeker' || profile.role === 'contractor') &&
      (!profile.years_of_experience || !profile.education_level || !profile.date_of_birth)) ||
    ((profile.role === 'employer' || profile.role === 'owner') &&
      !profile.is_private_household &&
      (!profile.business_name || !profile.industry || !profile.number_of_employees))
  );

  // Check if user needs to agree to terms (existing users who haven't agreed yet)
  // New users agree during registration, so they will have terms_agreed_at set
  const needsTermsAgreement = hasProfile && !needsRoleSelection && !needsProfileCompletion && profile.role !== 'peso_staff' &&
    (profile.terms_agreed_at === null || profile.terms_agreed_at === undefined || profile.terms_agreed_at === '');

  // Skills are now collected during signup (Auth.js + ProfileCompletion.js) — no separate skills screen needed
  const needsSkills = false;

  // Debug log for terms agreement check
  if (hasProfile) {
    console.log('[App] Terms check:', {
      terms_agreed_at: profile.terms_agreed_at,
      needsTermsAgreement,
      needsRoleSelection,
      needsProfileCompletion,
      needsSkills
    });
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        {/* WWW Redirect - handles boholtrabaho.com -> www.boholtrabaho.com */}
        <WwwRedirect />

        {/* NetworkStatusBanner removed - no offline mode */}

        <Toaster position="top-center" richColors />
        {/* PWAUpdatePrompt removed - no caching/updates */}
        <DebugPanel />
        <CookieConsent />

        <ChunkLoadErrorBoundary>
        {/* All interactive components INSIDE error boundary to prevent white screen on crash */}

        {/* LanguageSelectionModal moved to Jobs.js - only shows on landing/browse page */}

        {/* PWA Install Banner (mobile only, after login) */}
        <PWAInstallBanner profile={profile} />

        {/* Push Notification Prompt (shows after first login in PWA) */}
        <NotificationPromptModal
          profile={profile}
          isOpen={showPrompt}
          onClose={handleClose}
        />

        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Jobs profile={profile} setProfile={setProfile} />} />
          <Route path="/sign-up" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Auth />} />
          {/* Redirect old /auth to /sign-up while preserving query parameters (e.g., ?ref=CODE) */}
          <Route path="/auth" element={<AuthRedirect />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/role-selection" element={<RoleSelection />} />
        <Route path="/complete-profile" element={<ProfileCompletion />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/diagnostic-auth" element={<DiagnosticAuth />} />
        <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/jobs" element={<Jobs profile={profile} setProfile={setProfile} />} />
        <Route path="/services" element={<Services profile={profile} setProfile={setProfile} />} />
        {/* Services Marketplace listing routes. STATIC create route MUST be registered BEFORE the
            dynamic :slug route so it is not swallowed by it. The :id/edit route is more specific
            (trailing /edit) so ordering relative to :slug is safe. */}
        <Route path="/services/listings/create" element={
          isAuthenticated && hasProfile
            ? <CreateServiceListing profile={profile} setProfile={setProfile} />
            : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/sign-up" replace />
        } />
        <Route path="/services/listings/:id/edit" element={
          isAuthenticated && hasProfile
            ? <EditServiceListing profile={profile} setProfile={setProfile} />
            : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/sign-up" replace />
        } />
        <Route path="/services/listings/:slug" element={<ServiceListingDetail profile={profile} setProfile={setProfile} />} />
        {/* Service Requests. STATIC /new MUST be registered BEFORE the dynamic :id route so it is
            not swallowed by it. /new is the client "I need X" form (auth + profile required); the
            :id detail is role-adaptive and self-handles the no-session case. Both are flag-shelled. */}
        <Route path="/services/requests/new" element={
          isAuthenticated && hasProfile
            ? <PostServiceRequest profile={profile} setProfile={setProfile} />
            : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/sign-up" replace />
        } />
        <Route path="/services/requests/:id" element={<ServiceRequestDetail profile={profile} setProfile={setProfile} />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        {/* PESO Panglao co-branded registration portal (public) */}
        {/* Multi-LGU PESO portal — branding/fields loaded per office slug */}
        <Route path="/peso/:lgu" element={<PesoOfficeProvider><PanglaoPesoLanding profile={profile} setProfile={setProfile} /></PesoOfficeProvider>} />
        <Route path="/peso/:lgu/seeker" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <PesoOfficeProvider><PesoSeekerRegister /></PesoOfficeProvider>} />
        <Route path="/peso/:lgu/employer" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <PesoOfficeProvider><PesoEmployerRegister /></PesoOfficeProvider>} />
        {/* Legacy Panglao URLs (referenced in the MOA) -> /peso/panglao */}
        <Route path="/PanglaoPESO" element={<Navigate to="/peso/panglao" replace />} />
        <Route path="/PanglaoPESO/seeker" element={<Navigate to="/peso/panglao/seeker" replace />} />
        <Route path="/PanglaoPESO/employer" element={<Navigate to="/peso/panglao/employer" replace />} />
        <Route path="/panglaopeso" element={<Navigate to="/peso/panglao" replace />} />
        <Route path="/agree-terms" element={isAuthenticated ? <TermsAgreement /> : <Navigate to="/sign-up" replace />} />
        <Route path="/delete-data" element={<DataDeletion />} />
        <Route path="/notification-debug" element={<NotificationDebug />} />
        {/* Skills collected during signup — /add-skills redirects to dashboard */}
        <Route path="/add-skills" element={<Navigate to="/dashboard" replace />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            profileLoading ? (
              <div className="flex h-screen items-center justify-center">
                <div className="spinner" />
              </div>
            ) : isAuthenticated && hasProfile ? (
              // Redirect to role selection if role is pending or missing
              needsRoleSelection ? (
                <Navigate to="/role-selection" replace />
              ) : // Redirect to profile completion if mandatory fields are missing
              needsProfileCompletion ? (
                <Navigate to="/complete-profile" replace />
              ) : // Redirect to terms agreement if user hasn't agreed yet (existing users)
              needsTermsAgreement ? (
                <Navigate to="/agree-terms" replace />
              ) : needsSkills ? (
                <Navigate to="/add-skills" replace />
              ) : profile.role === 'peso_staff' ? (
                <PesoDashboard profile={profile} setProfile={setProfile} />
              ) : profile.role === 'admin' ? (
                <AdminDashboard profile={profile} setProfile={setProfile} />
              ) : (profile.role === 'job_seeker' || profile.role === 'contractor') ? (
                <DashboardSeeker profile={profile} setProfile={setProfile} />
              ) : (profile.role === 'employer' || profile.role === 'owner') ? (
                <DashboardEmployer profile={profile} setProfile={setProfile} />
              ) : (
                <Dashboard profile={profile} setProfile={setProfile} />
              )
            ) : isAuthenticated ? (
              // Authenticated but no profile - redirect to role selection to create profile
              <Navigate to="/role-selection" replace />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="/jobs/:slug" element={<JobDetails profile={profile} setProfile={setProfile} />} />
        <Route path="/gigs/:id" element={<GigDetails profile={profile} setProfile={setProfile} />} />
        <Route path="/employers/:employer_id" element={<EmployerProfile profile={profile} />} />
        <Route path="/post-job" element={
          isAuthenticated && hasProfile
            ? (profile?.role === 'employer' || profile?.role === 'owner' ? <PostJob profile={profile} setProfile={setProfile} /> : <Navigate to="/dashboard" replace />)
            : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/sign-up" replace />
        } />
        <Route path="/profile" element={isAuthenticated && hasProfile ? <ProfileNew profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/credits" element={isAuthenticated && hasProfile ? <Credits profile={profile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/messages" element={isAuthenticated && hasProfile ? <Messages profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/applied-jobs" element={isAuthenticated && hasProfile ? <AppliedJobs profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/verify-id" element={isAuthenticated && hasProfile ? <VerifyID profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/admin-panel" element={isAuthenticated && hasProfile ? <CEODashboard profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/admin" element={isAuthenticated && hasProfile ? <AdminDashboard profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/peso" element={isAuthenticated && hasProfile ? (profile?.role === 'peso_staff' ? <PesoDashboard profile={profile} setProfile={setProfile} /> : <Navigate to="/dashboard" replace />) : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/peso/nsrp/:seekerId" element={isAuthenticated && hasProfile ? (profile?.role === 'peso_staff' ? <PesoNsrpPrint /> : <Navigate to="/dashboard" replace />) : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/peso/nsrp2/:employerId" element={isAuthenticated && hasProfile ? (profile?.role === 'peso_staff' ? <PesoNsrp2Print /> : <Navigate to="/dashboard" replace />) : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/payment-success" element={isAuthenticated && hasProfile ? <PaymentSuccess profile={profile} setProfile={setProfile} /> : isAuthenticated ? <Navigate to="/role-selection" replace /> : <Navigate to="/" replace />} />
        <Route path="/payment-failed" element={isAuthenticated ? <PaymentFailed /> : <Navigate to="/" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </ChunkLoadErrorBoundary>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
